import { useState } from 'react';
import type { PersonEmail, PersonEmailLabel } from '@dsr/shared-types';
import {
  useCreatePersonEmail,
  useUpdatePersonEmail,
  useDeletePersonEmail,
} from '../api/queries/persons';
import { toast } from './Toasts';
import { ApiError } from '../api/client';
import { Button, StatusBadge, Table, FormField, Input, ConfirmDialog } from './ui';

// Editable emails for a person, wired to the per-row satellite endpoints
// (POST/PATCH/DELETE /api/persons/:id/emails, person.write). This is the
// dedicated per-row UI the registry expects — the shared person Edit form does
// NOT persist satellite rows. Adding a primary personal email here is the
// precondition for company-mailbox provisioning. Company-managed addresses
// (the mailbox itself) are shown read-only.

const LABEL_OPTIONS: { value: PersonEmailLabel; label: string }[] = [
  { value: 'personal', label: 'Personal' },
  { value: 'work', label: 'Work' },
  { value: 'company', label: 'Company' },
  { value: 'other', label: 'Other' },
];

const LABEL_TEXT: Record<PersonEmailLabel, string> = {
  personal: 'Personal',
  work: 'Work',
  company: 'Company',
  other: 'Other',
};

export function PersonEmailsManager({
  personId,
  emails,
}: {
  personId: number;
  emails: PersonEmail[];
}): JSX.Element {
  const create = useCreatePersonEmail();
  const update = useUpdatePersonEmail();
  const remove = useDeletePersonEmail();

  const [label, setLabel] = useState<PersonEmailLabel>('personal');
  const [email, setEmail] = useState('');
  const [primary, setPrimary] = useState(true);
  const [removing, setRemoving] = useState<PersonEmail | null>(null);

  const busy = create.isPending || update.isPending || remove.isPending;

  // Clear every other primary so a person never has two. Sequential is fine —
  // there is at most one existing primary in practice.
  const clearOtherPrimaries = async (exceptId: number | null): Promise<void> => {
    for (const e of emails) {
      if (e.is_primary && e.id !== exceptId) {
        await update.mutateAsync({ personId, satId: e.id, patch: { is_primary: false } });
      }
    }
  };

  const onAdd = async (): Promise<void> => {
    if (!email.trim()) return;
    try {
      if (primary) await clearOtherPrimaries(null);
      await create.mutateAsync({ personId, input: { label, email: email.trim(), is_primary: primary } });
      setEmail('');
      toast.success('Email added.');
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Failed to add email.');
    }
  };

  const makePrimary = async (target: PersonEmail): Promise<void> => {
    try {
      await clearOtherPrimaries(target.id);
      await update.mutateAsync({ personId, satId: target.id, patch: { is_primary: true } });
      toast.success('Primary email updated.');
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Failed to update.');
    }
  };

  const onRemove = async (): Promise<void> => {
    if (!removing) return;
    try {
      await remove.mutateAsync({ personId, satId: removing.id });
      toast.success('Email removed.');
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Failed to remove.');
    } finally {
      setRemoving(null);
    }
  };

  return (
    <section className="detail-card">
      <h2>Emails</h2>
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.HeaderCell>Label</Table.HeaderCell>
            <Table.HeaderCell>Email</Table.HeaderCell>
            <Table.HeaderCell>Primary</Table.HeaderCell>
            <Table.HeaderCell />
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {emails.length === 0 && (
            <Table.Empty colSpan={4}>No emails on file.</Table.Empty>
          )}
          {emails.map((e) => (
            <Table.Row key={e.id}>
              <Table.Cell tone="strong">
                {LABEL_TEXT[e.label]}
                {e.is_company_managed && <StatusBadge status="new">Managed</StatusBadge>}
              </Table.Cell>
              <Table.Cell>{e.email}</Table.Cell>
              <Table.Cell>{e.is_primary ? <StatusBadge status="active">Primary</StatusBadge> : '—'}</Table.Cell>
              <Table.Cell className="table-actions">
                {!e.is_company_managed && (
                  <>
                    {!e.is_primary && (
                      <Button variant="ghost" className="btn-sm" disabled={busy} onClick={() => void makePrimary(e)}>
                        Make primary
                      </Button>
                    )}
                    <Button variant="ghost" className="btn-sm" disabled={busy} onClick={() => setRemoving(e)}>
                      Remove
                    </Button>
                  </>
                )}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      <form
        className="form-grid"
        style={{ marginTop: '0.75rem' }}
        onSubmit={(ev) => { ev.preventDefault(); void onAdd(); }}
      >
        <FormField label="Label">
          <select className="form-input" value={label} onChange={(e) => setLabel(e.target.value as PersonEmailLabel)}>
            {LABEL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Email">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" />
        </FormField>
        <FormField label="Primary">
          <span className="checkbox-field">
            <input type="checkbox" checked={primary} onChange={(e) => setPrimary(e.target.checked)} />
            <span>Set as primary</span>
          </span>
        </FormField>
        <div className="settings-actions">
          <Button type="submit" variant="primary" className="btn-sm" disabled={busy || !email.trim()}>
            {create.isPending ? 'Adding…' : 'Add email'}
          </Button>
        </div>
      </form>

      <ConfirmDialog
        open={removing !== null}
        title="Remove email"
        message={`Remove ${removing?.email ?? 'this email'}?`}
        confirmLabel="Remove"
        danger
        busy={remove.isPending}
        onConfirm={() => void onRemove()}
        onCancel={() => setRemoving(null)}
      />
    </section>
  );
}
